import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SealCheck, XCircle, MagnifyingGlass, CircleNotch, ShieldCheck } from "@phosphor-icons/react";

export default function VerifyCertificate() {
  const [params] = useSearchParams();
  const [folio, setFolio] = useState(params.get("folio") || "");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const verify = async (f) => {
    const code = (f || folio).trim();
    if (!code) return;
    setLoading(true); setSearched(true);
    try { const { data } = await api.get(`/verify/${code}`); setResult(data); }
    catch { setResult({ valid: false }); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (params.get("folio")) verify(params.get("folio")); }, []);

  const fmt = (d) => new Date(d).toLocaleDateString("es-CL", { day: "2-digit", month: "long", year: "numeric" });

  return (
    <div className="max-w-2xl mx-auto px-6 py-14">
      <div className="text-center mb-10">
        <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center mx-auto mb-4">
          <ShieldCheck size={30} className="text-primary" weight="duotone" />
        </div>
        <p className="text-xs uppercase tracking-overline text-primary mb-2 font-mono">Verificación</p>
        <h1 className="font-display text-3xl sm:text-4xl font-800 tracking-tight">Verifica un certificado</h1>
        <p className="text-muted-foreground mt-3">Ingresa el folio del certificado para comprobar su autenticidad.</p>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); verify(); }} className="flex gap-2 mb-8">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={folio} onChange={(e) => setFolio(e.target.value.toUpperCase())}
            placeholder="Ej: CC-2026-XXXXXXXX" className="pl-9 font-mono" data-testid="verify-folio-input" />
        </div>
        <Button type="submit" disabled={loading} data-testid="verify-submit"
          className="bg-primary hover:bg-blue-600 text-white">
          {loading ? <CircleNotch className="animate-spin" /> : "Verificar"}
        </Button>
      </form>

      {searched && !loading && result && (
        result.valid ? (
          <div className="bg-white border-2 border-primary/20 rounded-lg p-8 animate-fade-up" data-testid="verify-valid">
            <div className="flex items-center gap-3 mb-6">
              <SealCheck size={40} weight="fill" className="text-primary" />
              <div>
                <p className="font-display font-800 text-xl">Certificado auténtico</p>
                <p className="text-sm text-muted-foreground">Emitido por NovaCert OTEC</p>
              </div>
            </div>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between border-b border-border pb-2"><dt className="text-muted-foreground">Folio</dt><dd className="font-mono font-medium">{result.folio}</dd></div>
              <div className="flex justify-between border-b border-border pb-2"><dt className="text-muted-foreground">Curso</dt><dd className="font-medium text-right">{result.course_name}</dd></div>
              <div className="flex justify-between border-b border-border pb-2"><dt className="text-muted-foreground">Horas</dt><dd className="font-medium">{result.hours} h</dd></div>
              <div className="flex justify-between border-b border-border pb-2"><dt className="text-muted-foreground">Alumno</dt><dd className="font-medium">{result.student_initials} · (protegido)</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Fecha de emisión</dt><dd className="font-medium">{fmt(result.issued_at)}</dd></div>
            </dl>
          </div>
        ) : (
          <div className="bg-white border-2 border-destructive/20 rounded-lg p-8 text-center animate-fade-up" data-testid="verify-invalid">
            <XCircle size={48} weight="fill" className="text-destructive mx-auto mb-4" />
            <p className="font-display font-800 text-xl">Certificado no encontrado</p>
            <p className="text-sm text-muted-foreground mt-2">No existe un certificado con ese folio. Verifica el código ingresado.</p>
          </div>
        )
      )}
    </div>
  );
}
